import React from 'react';
import { useDispatch } from 'react-redux';
import classnames from 'classnames';

// Types
import { Workspace } from 'Shared/types/Workspace.type';
import { AgendaBuilderSteps } from './AgendaBuilder';

// Hooks
import { useToggle } from 'Shared/utils/hooks';

// Actions
import { cancelTemplateWizardAction } from 'Shared/actions/workspaces';

// Components
import { WpModal, WpModalBody } from 'Wpds/components/modal';
import { WpPageLayout, WpPageHeader, WpPageTitle, WpPageBody } from 'Wpds/components/page-layout';
import { WpButton, WpButtonGroup } from 'Wpds/components/button';
import ConfirmModal from 'Shared/components/ConfirmModal/ConfirmModal';

// Styles
import './AgendaBuilderModalWrapper.scss';

type Props = {
  workspace: Workspace;
  children: React.ReactNode;
  handleSetState: Function;
  nextBtnLabel?: string;
  showLoadingOnPrimaryCta?: boolean;
  handleContinueClick: () => void;
  handleBackClick?: () => void;
  blockScroll?: boolean;
};

const AgendaBuilderModalWrapper = ({
  workspace,
  children,
  handleSetState,
  nextBtnLabel = 'Next',
  showLoadingOnPrimaryCta = false,
  handleContinueClick,
  handleBackClick,
  blockScroll = false,
}: Props) => {
  const dispatch = useDispatch();

  const [isConfirmCancelModalOpen, toggleIsConfirmModalOpen] = useToggle(false);
  const [isCancelling, toggleIsCancelling] = useToggle(false);

  const handleCancelClick = () => {
    toggleIsCancelling();
    dispatch(cancelTemplateWizardAction({ workspaceId: workspace.id })).then(() => {
      toggleIsCancelling();
      toggleIsConfirmModalOpen();
      handleSetState(AgendaBuilderSteps.start);
    });
  };

  const baseClassName = 'AgendaBuilderModalWrapper';
  const classes = classnames(baseClassName, blockScroll ? `${baseClassName}--no-scroll` : '');

  return (
    <>
      <WpModal isOpen size="full">
        <WpModalBody>
          <WpPageLayout width="container" className={classes}>
            <WpPageHeader sticky>
              <WpPageTitle
                title="Create Template"
                buttonsContainer={
                  <WpButtonGroup>
                    <WpButton variant="tertiary" onClick={toggleIsConfirmModalOpen}>
                      Cancel
                    </WpButton>
                    {handleBackClick && (
                      <WpButton variant="secondary" onClick={handleBackClick}>
                        Back
                      </WpButton>
                    )}
                    <WpButton onClick={handleContinueClick} loading={showLoadingOnPrimaryCta}>
                      {nextBtnLabel}
                    </WpButton>
                  </WpButtonGroup>
                }
              />
            </WpPageHeader>
            <WpPageBody>{children}</WpPageBody>
          </WpPageLayout>
        </WpModalBody>
      </WpModal>
      {isConfirmCancelModalOpen && (
        <ConfirmModal
          isOpen={isConfirmCancelModalOpen}
          onToggle={toggleIsConfirmModalOpen}
          title="Cancel"
          message="Cancel template creation? The changes you've made will be lost."
          onConfirmClick={handleCancelClick}
          isProcessing={isCancelling}
        />
      )}
    </>
  );
};

export default AgendaBuilderModalWrapper;
